/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { ApiError, OrderService } from '@vitafy/storefront-api-contracts-fetch';
import { useEffect, useRef, useState } from 'react';

import { useIntersection } from '@common/hooks/useIntersection';
import { sendGAEvent } from '@common/utlis';
import Button from '@stories/atoms/Button';
import Alert from '@stories/atoms/Icons/alert.svg';
import Spinner from '@stories/atoms/Spinner';

import type { OrderStatusDto } from '@vitafy/storefront-api-contracts-fetch';
import type { Dispatch, FC, SetStateAction, SyntheticEvent } from 'react';

const FormStyles = {
  trackingTab: css({
    display: 'flex',
    gap: '1.5rem',
    justifyContent: 'center'
  }),
  trackingTabChild: (t) =>
    css({
      cursor: 'pointer',
      fontWeight: 'bold',
      fontSize: t.font.size.small,
      padding: '0.5rem',
      textAlign: 'center',
      textWrap: 'nowrap'
    }),
  trackingTabChildSelected: (t) =>
    css({
      borderBottom: `2px solid ${t.color.primary}`,
      color: t.color.primary,
      cursor: 'pointer',
      fontSize: t.font.size.small,
      fontWeight: 'bold',
      padding: '0.5rem',
      textAlign: 'center',
      textWrap: 'nowrap'
    }),
  mainDiv: css({
    display: 'none',
    flexDirection: 'column'
  }),
  mainDivActive: css({
    display: 'flex',
    flexDirection: 'column'
  }),
  childDiv: css({
    display: 'flex',
    flexDirection: 'column'
  }),
  childInput: (t) =>
    css({
      background: t.color.grey.g97,
      border: `1px solid ${t.color.grey.g92}`,
      borderRadius: '4px',
      fontSize: t.font.size.small,
      marginBottom: '1rem',
      padding: '1rem'
    })
};

type TTrackingForm = {
  setPage: Dispatch<SetStateAction<string>>;
  setTrackingData: Dispatch<SetStateAction<OrderStatusDto | undefined>>;
};

const TrackingForm: FC<TTrackingForm> = ({ setPage, setTrackingData }) => {
  const sectionRef = useRef(null);
  useIntersection(sectionRef, () =>
    sendGAEvent({ category: 'Order Tracking', action: 'Module Impression', label: 'Order Tracking Form' })
  );
  const [activeTab, setActiveTab] = useState('order_number');
  const [FormResult, setFormResult] = useState({
    status: true,
    message: ''
  });
  const [loading, setLoading] = useState(false);

  // removes the warning when tabs are switched
  useEffect(() => {
    setFormResult({ status: true, message: '' });
  }, [activeTab]);

  const handleSubmit_OrderNumber = async (e: SyntheticEvent) => {
    e.preventDefault();
    setLoading(true);
    const target = e.target as typeof e.target & {
      order_number: { value: string };
      zip_code: { value: string };
    };
    const orderNumber = target.order_number.value;
    const zipCode = target.zip_code.value;
    try {
      const trackingData = await OrderService.orderTrackingControllerGetTrackingInfo({
        orderNumber: orderNumber,
        postcode: zipCode
      });
      setPage('results');
      setTrackingData(trackingData);

      // Send GA event
      sendGAEvent({
        category: 'Order Tracking',
        action: 'Submit',
        label: 'Form Submitted - Order Number'
      });
    } catch (error) {
      if (error instanceof ApiError) {
        const responseStatus = error.status;
        console.log('API call failed with status code:', responseStatus);

        sendGAEvent({
          category: 'Order Tracking',
          action: 'API Call Failure',
          label: 'OrderTrackingForm - Order Number',
          additionalTrackingParameters: {
            errorStatus: responseStatus
          }
        });

        setFormResult(() => {
          return {
            status: false,
            message:
              responseStatus === 500
                ? 'Der Server ist vorübergehend ausgefallen, bitte versuchen Sie es später noch einmal.'
                : 'Wir konnten deine Bestellung nicht finden. Bitte überprüfe deine Bestellnummer oder Sendungsnummer.'
          };
        });
      } else {
        console.error('API call failed:', error);
      }
    } finally {
      setLoading(false); // Set loading back to false
    }
  };

  const handleSubmit_TrackingNumber = async (e: SyntheticEvent) => {
    e.preventDefault();
    setLoading(true);
    const target = e.target as typeof e.target & {
      tracking_number: { value: string };
    };
    const trackingNumber = target.tracking_number.value;
    try {
      const trackingData = await OrderService.orderTrackingControllerGetTrackingInfo({
        trackingNumber: trackingNumber
      });
      setPage('results');
      setTrackingData(trackingData);

      // Send GA event
      sendGAEvent({
        category: 'Order Tracking',
        action: 'Submit',
        label: 'Form Submitted - Tracking Number'
      });
    } catch (error) {
      if (error instanceof ApiError) {
        const responseStatus = error.status;
        console.log('API call failed with status code:', responseStatus);

        sendGAEvent({
          category: 'Order Tracking',
          action: 'API Call Failure',
          label: 'OrderTrackingForm - Tracking Number',
          additionalTrackingParameters: {
            errorStatus: responseStatus
          }
        });

        setFormResult(() => {
          return {
            status: false,
            message:
              responseStatus === 500
                ? 'Der Server ist vorübergehend ausgefallen, bitte versuchen Sie es später noch einmal.'
                : 'Wir konnten deine Bestellung nicht finden. Bitte überprüfe deine Bestellnummer oder Sendungsnummer.'
          };
        });
      } else {
        console.error('API call failed:', error);
      }
    } finally {
      setLoading(false); // Set loading back to false
    }
  };

  return (
    <div
      ref={sectionRef}
      css={{
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: '2rem',
        width: '100%'
      }}
    >
      <div
        css={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          width: '100%'
        }}
      >
        <div>
          <h2
            css={(t) => ({
              ...t.headline.h3,
              textWrap: 'nowrap'
            })}
          >
            Bestellstatus
          </h2>
        </div>
        <div
          css={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: '2.5rem',
            maxWidth: '462px',
            width: '100%'
          }}
        >
          <div css={FormStyles.trackingTab}>
            <div
              css={
                activeTab == 'order_number'
                  ? FormStyles.trackingTabChildSelected
                  : FormStyles.trackingTabChild
              }
              onClick={() => setActiveTab('order_number')}
            >
              <span>Bestellnummer</span>
            </div>
            <div
              css={
                activeTab == 'tracking_number'
                  ? FormStyles.trackingTabChildSelected
                  : FormStyles.trackingTabChild
              }
              onClick={() => setActiveTab('tracking_number')}
            >
              <span>Sendungsnummer</span>
            </div>
          </div>
          <div
            css={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              width: '100%'
            }}
          >
            <form
              autoComplete="off"
              css={activeTab == 'order_number' ? FormStyles.mainDivActive : FormStyles.mainDiv}
              onSubmit={handleSubmit_OrderNumber}
            >
              <h3
                css={(t) => ({
                  ...t.font.copy,
                  fontWeight: 'bold',
                  marginBottom: '1rem'
                })}
              >
                Bestellnummer
              </h3>
              <div css={FormStyles.childDiv}>
                <label htmlFor="order_number" />
                <input
                  css={FormStyles.childInput}
                  name="order_number"
                  placeholder="Bestellnummer eingeben"
                  type="text"
                ></input>
                <label htmlFor="zip_code" />
                <input
                  css={FormStyles.childInput}
                  name="zip_code"
                  placeholder="PLZ eingegen"
                  type="text"
                ></input>
              </div>
              {FormResult.status == false && (
                <div
                  css={(t) => ({
                    color: t.color.error,
                    display: 'flex',
                    fontWeight: 'bold',
                    gap: '0.5rem',
                    justifyContent: 'flex-start',
                    width: '100%'
                  })}
                >
                  <Alert css={{ minWidth: '20px', minHeight: '20px' }} />
                  <p>{FormResult.message}</p>
                </div>
              )}
              <div
                css={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '1.5rem',
                  width: '100%'
                }}
              >
                {loading ? (
                  <button
                    type="button"
                    css={(t) => ({
                      ...t.button.primary.solid,
                      borderRadius: '0.5rem',
                      color: 'white',
                      padding: '0.5rem',
                      width: '225px', // is it fine to use fixed width here?
                      zIndex: 1
                    })}
                  >
                    <Spinner size="8px" />
                  </button>
                ) : (
                  <Button
                    attrs={{ type: 'submit' }}
                    buttonStyle="primary"
                    cssProps={{ width: '225px' }} // is it fine to use fixed width here?
                    label="SUCHEN"
                  />
                )}
              </div>
            </form>
            <form
              autoComplete="off"
              css={activeTab == 'tracking_number' ? FormStyles.mainDivActive : FormStyles.mainDiv}
              onSubmit={handleSubmit_TrackingNumber}
            >
              <h3
                css={(t) => ({
                  ...t.font.copy,
                  fontWeight: 'bold',
                  marginBottom: '1rem'
                })}
              >
                Sendungsnummer
              </h3>
              <div css={FormStyles.childDiv}>
                <label htmlFor="tracking_number" />
                <input
                  type="text"
                  name="tracking_number"
                  placeholder="Sendungsnummer"
                  css={FormStyles.childInput}
                ></input>
              </div>
              {FormResult.status == false && (
                <div
                  css={(t) => ({
                    color: t.color.error,
                    display: 'flex',
                    fontWeight: 'bold',
                    gap: '0.5rem',
                    justifyContent: 'flex-start',
                    width: '100%'
                  })}
                >
                  <Alert css={{ minWidth: '20px', minHeight: '20px' }} />
                  <p>{FormResult.message}</p>
                </div>
              )}
              <div
                css={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                  marginTop: '1.5rem'
                }}
              >
                {loading ? (
                  <button
                    type="button"
                    css={(t) => ({
                      ...t.button.primary.solid,
                      borderRadius: '0.5rem',
                      color: 'white',
                      padding: '0.5rem',
                      width: '225px', // is it fine to use fixed width here?
                      zIndex: 1
                    })}
                  >
                    <Spinner size="8px" />
                  </button>
                ) : (
                  <Button
                    attrs={{ type: 'submit' }}
                    buttonStyle="primary"
                    cssProps={{ width: '225px' }} // is it fine to use fixed width here? or use padding instead?
                    label="SUCHEN"
                  />
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="contact">
        <p
          css={{
            textAlign: 'center'
          }}
        >
          Hast du noch Fragen?
          <br /> Dann schreibe uns gerne an!
        </p>
        <div
          css={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '1rem',
            width: '100%'
          }}
          onClick={() =>
            sendGAEvent({
              category: 'Order Tracking',
              action: 'Click',
              label: 'Hilfe & Kontakt - Form Page'
            })
          }
        >
          <Button
            attrs={{ type: 'button', target: '_blank' }}
            buttonStyle="primary"
            href="/faq"
            label="HILFE & KONTAKT"
            outline
          />
        </div>
      </div>
    </div>
  );
};

export default TrackingForm;
